import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Button variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'whitespace-nowrap rounded-max font-semibold font-body',
    'transition-colors duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-40',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:bg-unset dark:text-unset',
  ],
  {
    variants: {
      variant: {
        primaryFill: [
          'bg-primary-700 text-white',
          'hover:bg-primary-500',
          'active:bg-primary-900',
          'aria-pressed:bg-primary-900',
        ],
        primaryOutline: [
          'bg-white text-primary-700',
          'border-2 border-primary-700',
          'hover:bg-neutral-50 hover:text-subtext',
          'active:bg-neutral-100 active:text-black',
          'aria-pressed:bg-neutral-100',
        ],
        blackFill: [
          'bg-black text-white',
          'hover:bg-neutral-500',
          'active:bg-neutral-700',
          'aria-pressed:bg-neutral-700',
        ],
        blackOutline: [
          'bg-white text-black',
          'border-2 border-black',
          'hover:bg-neutral-50',
          'active:bg-neutral-100',
          'aria-pressed:bg-neutral-100',
        ],
        grayOutline: [
          'bg-white text-black',
          'border-2 border-neutral-200',
          'hover:bg-neutral-50',
          'active:bg-neutral-100',
          'aria-pressed:bg-neutral-100',
        ],
        textOnly: [
          'bg-transparent text-primary-700',
          'underline-offset-4 hover:underline',
          'hover:text-subtext active:text-black',
          'aria-pressed:text-black',
          '!p-0',
        ],
        textOnlyNeutral: [
          'bg-transparent text-body',
          'underline-offset-4 hover:underline',
          'hover:text-subtext active:text-black',
          '!p-0',
        ],
        textOnlySecondary: [
          'bg-transparent text-secondary-700',
          'underline-offset-4 hover:underline',
          'hover:text-subtext active:text-black',
          'aria-pressed:text-black',
          '!p-0',
        ],
        textOnlyWhite: [
          'bg-transparent text-white',
          'underline-offset-4 hover:underline',
          'hover:text-white active:text-white',
          'aria-pressed:text-white',
          '!p-0',
        ],
      },
      size: {
        sm: 'h-10.5 px-4 py-2.5 text-b5 leading-b5',
        lg: 'h-11 px-6 py-2.5 text-b4 leading-b4',
      },
    },
    defaultVariants: {
      variant: 'primaryFill',
      size: 'sm',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
